import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, FolderKanban, Trash2, Edit3, Download, Search, Users } from "lucide-react";
import { toast } from "sonner";
import Pagination, { usePagination } from "@/components/Pagination";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", code: "", active: true, default_retention_heads: [], tagged_user_ids: [] };

export default function Projects() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [heads, setHeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [userQ, setUserQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/projects");
    setRows(data);
  };
  useEffect(() => {
    load();
    api.get("/roles/modules").then(({ data }) => setHeads(data.retention_heads || [])).catch(() => {});
    api.get("/projects/taggable-users").then(({ data }) => setUsers(data || [])).catch(() => {});
  }, []);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((p) => {
      const hh = Array.isArray(p.default_retention_heads) ? p.default_retention_heads.join(" ") : "";
      const hay = `${p.name || ""} ${p.code || ""} ${hh}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  const {
    pageItems: pagedRows,
    page: prPage, setPage: setPrPage,
    pageSize: prPageSize, setPageSize: setPrPageSize,
    totalPages: prTotalPages, totalItems: prTotalItems,
  } = usePagination(filteredRows, "voltledger.projects.pageSize");

  const startCreate = () => { setEditing(null); setForm(EMPTY); setUserQ(""); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      code: p.code || "",
      active: p.active,
      default_retention_heads: p.default_retention_heads || [],
      tagged_user_ids: p.tagged_user_ids || [],
    });
    setUserQ("");
    setOpen(true);
  };

  const toggleHead = (h) => {
    setForm((f) => {
      const set = new Set(f.default_retention_heads);
      if (set.has(h)) set.delete(h); else set.add(h);
      return { ...f, default_retention_heads: Array.from(set) };
    });
  };

  const toggleUser = (uid) => {
    setForm((f) => {
      const set = new Set(f.tagged_user_ids || []);
      if (set.has(uid)) set.delete(uid); else set.add(uid);
      return { ...f, tagged_user_ids: Array.from(set) };
    });
  };

  const filteredUsers = useMemo(() => {
    const needle = userQ.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) =>
      `${u.name || ""} ${u.email || ""} ${u.role_name || ""}`.toLowerCase().includes(needle)
    );
  }, [users, userQ]);

  const userNameMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u.name || u.email; });
    return m;
  }, [users]);

  const submit = async () => {
    try {
      if (!form.name.trim()) return toast.error("Project name is required");
      if (editing) {
        await api.put(`/projects/${editing.id}`, form);
        toast.success("Project updated");
      } else {
        await api.post("/projects", form);
        toast.success("Project added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get("/exports/projects", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `projects_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Projects exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const canCreate = hasPerm("projects", "create");
  const canEdit = hasPerm("projects", "edit");
  const canDelete = hasPerm("projects", "delete");

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1100px]">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-600/90">Master Data</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Projects</h1>
          <p className="text-sm text-slate-500 mt-2">
            Single source of truth for project names. Invoice entries and Excel uploads must match these names exactly (case-sensitive).
            {q && <span className="ml-2 text-indigo-600 font-semibold">· {filteredRows.length} match{filteredRows.length === 1 ? "" : "es"}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-72">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Search project name, code, head…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 pr-9 h-10 text-sm border-slate-300 focus-visible:ring-[#002FA7] focus-visible:border-[#002FA7]"
              data-testid="projects-search"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-700 text-xs font-semibold"
                data-testid="projects-search-clear"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <Button onClick={downloadExcel} variant="outline" className="rounded-none h-10 border-slate-300" data-testid="export-projects-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export Excel
          </Button>
          {canCreate && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none h-10" data-testid="add-project-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Project
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">{editing ? "Edit Project" : "Add Project"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Project Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="project-name" placeholder="e.g. TPNODL-BNED MMG" />
                    <div className="text-[10px] text-slate-400 mt-1">Names must match exactly (case-sensitive).</div>
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Code (optional)</Label>
                    <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-none mt-1" data-testid="project-code" />
                  </div>
                </div>
                <div className="flex items-center justify-between border border-slate-200 px-3 py-2">
                  <Label className="text-sm">Active</Label>
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="project-active" />
                </div>

                {/* Tag Users */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" /> Tag Users to this Project
                    </Label>
                    <span className="text-[10px] text-slate-500 font-semibold" data-testid="tagged-user-count">
                      {(form.tagged_user_ids || []).length} selected
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400 mb-2">
                    Selected users will automatically get this project added to their allowed-projects list (skips users with unrestricted access).
                  </div>
                  <div className="relative mb-2">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      placeholder="Search active users by name, email, role…"
                      value={userQ}
                      onChange={(e) => setUserQ(e.target.value)}
                      className="rounded-none pl-8 h-9 text-xs border-slate-300"
                      data-testid="project-user-search"
                    />
                  </div>
                  <div className="border border-slate-200 max-h-56 overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <div className="text-center text-xs text-slate-400 py-6">
                        {users.length === 0 ? "No active users available." : "No users match the search."}
                      </div>
                    ) : (
                      filteredUsers.map((u) => {
                        const checked = (form.tagged_user_ids || []).includes(u.id);
                        return (
                          <label
                            key={u.id}
                            className={`flex items-center gap-2.5 px-3 py-1.5 text-xs cursor-pointer border-b border-slate-100 last:border-b-0 ${checked ? "bg-indigo-50/60" : "hover:bg-slate-50"}`}
                            data-testid={`project-user-row-${u.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleUser(u.id)}
                              data-testid={`project-user-toggle-${u.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-800 truncate">{u.name || "(unnamed)"}</div>
                              <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                            </div>
                            {u.role_name && (
                              <span className="px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded whitespace-nowrap">
                                {u.role_name}
                              </span>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                {heads.length > 0 && (
                  <div>
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Default Retention Heads</Label>
                    <div className="text-[10px] text-slate-400 mb-2">Used to auto-suggest hold heads on Bulk Payment for invoices in this project.</div>
                    <div className="border border-slate-200 p-3 grid grid-cols-2 gap-1.5">
                      {heads.map((h) => (
                        <label key={h} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1.5 py-1">
                          <Checkbox
                            checked={form.default_retention_heads.includes(h)}
                            onCheckedChange={() => toggleHead(h)}
                            data-testid={`proj-head-${h}`}
                          />
                          <span>{h}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={submit} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="submit-project">
                  {editing ? "Update" : "Add Project"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="card-surface rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Project</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Code</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Default Retention Heads</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Tagged Users</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-12">
                <FolderKanban className="w-8 h-8 mx-auto mb-2 text-slate-300" /> {q ? `No projects match "${q}".` : "No projects defined yet."}
              </td></tr>
            )}
            {pagedRows.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`project-row-${p.name}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{p.code || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`status-pill ${p.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {p.active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {(p.default_retention_heads || []).length === 0 ? (
                    <span className="text-slate-300 text-xs">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {(p.default_retention_heads || []).map((h) => (
                        <span key={h} className="px-1.5 py-0.5 text-[10px] bg-indigo-50 text-indigo-700 rounded whitespace-nowrap">{h}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {(() => {
                    const tagged = p.tagged_user_ids || [];
                    if (tagged.length === 0) return <span className="text-slate-300 text-xs">—</span>;
                    const shown = tagged.slice(0, 3);
                    const more = tagged.length - shown.length;
                    return (
                      <div className="flex flex-wrap gap-1" data-testid={`project-tagged-${p.name}`}>
                        {shown.map((uid) => (
                          <span key={uid} className="px-1.5 py-0.5 text-[10px] bg-amber-50 text-amber-800 border border-amber-200 rounded whitespace-nowrap">
                            {userNameMap[uid] || uid.slice(0, 6)}
                          </span>
                        ))}
                        {more > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded whitespace-nowrap">+{more} more</span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canEdit && (
                    <button onClick={() => startEdit(p)} className="text-slate-600 hover:text-[#002FA7] inline-flex items-center gap-1 text-xs font-semibold mr-3" data-testid={`edit-project-${p.name}`}>
                      <Edit3 className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => del(p)} className="text-red-500 hover:text-red-700 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`del-project-${p.name}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={prPage} setPage={setPrPage}
          pageSize={prPageSize} setPageSize={setPrPageSize}
          totalPages={prTotalPages} totalItems={prTotalItems}
          testId="projects-pagination"
        />
      </div>
    </div>
  );
}
